import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, registerables } from 'chart.js';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Badge } from '@/components/common/Badge';
import { Alert } from '@/components/common/Alert';
import { productService } from '@/services/product.service';
import { Product } from '@/types/product.types';
import {
  XMarkIcon,
  ShoppingBagIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  EyeIcon,
  TrashIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

Chart.register(...registerables);

interface StockItem extends Product {
  minStock?: number; // Seuil minimum de stock (peut être stocké localement ou dans la DB)
}

type StockStatus = 'in-stock' | 'low' | 'out';

const ITEMS_PER_PAGE = 10;

export const GestionStock = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StockStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingProduct, setEditingProduct] = useState<StockItem | null>(null);
  const [editQuantity, setEditQuantity] = useState<number>(0);
  const [editMinStock, setEditMinStock] = useState<number>(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailModalOpen, setDetailModalOpen] = useState<StockItem | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState<StockItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minQuantityFilter, setMinQuantityFilter] = useState<string>('');
  const [maxQuantityFilter, setMaxQuantityFilter] = useState<string>('');
  
  // Références pour les graphiques
  const stockStatusChartRef = useRef<HTMLCanvasElement>(null);
  const stockDistributionChartRef = useRef<HTMLCanvasElement>(null);
  const stockStatusChartInstance = useRef<Chart | null>(null);
  const stockDistributionChartInstance = useRef<Chart | null>(null);

  // Charger les produits
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getAll();
      
      // Initialiser minStock à 50 par défaut si non défini (peut être stocké dans localStorage ou DB)
      const productsWithMinStock = data.map(product => ({
        ...product,
        minStock: getMinStockFromStorage(product.id) || 50
      }));
      
      setProducts(productsWithMinStock);
      
      // Extraire les catégories uniques
      const uniqueCategories = Array.from(new Set(
        data
          .map(p => p.category)
          .filter((cat): cat is string => cat !== null && cat !== '')
      ));
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer le seuil minimum depuis localStorage (temporaire, devrait être dans la DB)
  const getMinStockFromStorage = (productId: number): number | null => {
    const stored = localStorage.getItem(`minStock_${productId}`);
    return stored ? parseInt(stored, 10) : null;
  };

  // Sauvegarder le seuil minimum dans localStorage
  const saveMinStockToStorage = (productId: number, minStock: number) => {
    localStorage.setItem(`minStock_${productId}`, minStock.toString());
  };

  // Calculer le statut du stock
  const calculateStockStatus = (product: StockItem): StockStatus => {
    if (product.stock_unlimited) return 'in-stock';
    
    const quantity = product.stock_quantity || 0;
    const minStock = product.minStock || 50;
    
    if (quantity === 0) return 'out';
    if (quantity <= minStock) return 'low';
    return 'in-stock';
  };

  // Filtrer les produits
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Filtre par recherche
      const matchesSearch = 
        !searchTerm ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.category && product.category.toLowerCase().includes(searchTerm.toLowerCase()));

      // Filtre par statut
      const status = calculateStockStatus(product);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      // Filtre par catégorie
      const matchesCategory = 
        categoryFilter === 'all' || 
        (product.category && product.category === categoryFilter);

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [products, searchTerm, statusFilter, categoryFilter]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, categoryFilter, minQuantityFilter, maxQuantityFilter]);

  // Calculer les statistiques
  const stats = useMemo(() => ({
    total: products.length,
    inStock: products.filter(p => calculateStockStatus(p) === 'in-stock').length,
    low: products.filter(p => calculateStockStatus(p) === 'low').length,
    out: products.filter(p => calculateStockStatus(p) === 'out').length,
  }), [products]);

  // Gérer la suppression d'un produit
  const handleDeleteProduct = async () => {
    if (!deleteModalOpen) return;
    
    try {
      setDeleting(true);
      await productService.delete(deleteModalOpen.id);
      await loadProducts();
      setDeleteModalOpen(null);
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      setError(`Erreur lors de la suppression: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Créer les graphiques Chart.js
  useEffect(() => {
    if (loading || !products.length) return;

    // Graphique 1: Répartition par statut de stock (doughnut)
    if (stockStatusChartRef.current) {
      const ctx = stockStatusChartRef.current.getContext('2d');
      if (ctx) {
        if (stockStatusChartInstance.current) {
          stockStatusChartInstance.current.destroy();
        }
        
        stockStatusChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: [t('adminStock.chartInStock'), t('adminStock.chartLow'), t('adminStock.chartOut')],
            datasets: [{
              data: [stats.inStock, stats.low, stats.out],
              backgroundColor: ['#4CAF50', '#FF9800', '#F44336'],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 15, usePointStyle: true }
              },
              tooltip: {
                callbacks: {
                  label: function(context) {
                    const label = context.label || '';
                    const value = context.parsed || 0;
                    const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                    return `${label}: ${value} (${percentage}%)`;
                  }
                }
              }
            }
          }
        });
      }
    }

    // Graphique 2: Distribution des quantités de stock (bar)
    if (stockDistributionChartRef.current) {
      const ctx = stockDistributionChartRef.current.getContext('2d');
      if (ctx) {
        if (stockDistributionChartInstance.current) {
          stockDistributionChartInstance.current.destroy();
        }
        
        // Grouper les produits par tranches de stock
        const stockRanges = {
          '0': 0,
          '1-50': 0,
          '51-100': 0,
          '101-200': 0,
          '200+': 0,
        };
        
        products.forEach(product => {
          if (product.stock_unlimited) {
            stockRanges['200+']++;
          } else {
            const qty = product.stock_quantity || 0;
            if (qty === 0) stockRanges['0']++;
            else if (qty <= 50) stockRanges['1-50']++;
            else if (qty <= 100) stockRanges['51-100']++;
            else if (qty <= 200) stockRanges['101-200']++;
            else stockRanges['200+']++;
          }
        });
        
        stockDistributionChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['0', '1-50', '51-100', '101-200', '200+'],
            datasets: [{
              label: t('adminStock.chartProductCount'),
              data: [
                stockRanges['0'],
                stockRanges['1-50'],
                stockRanges['51-100'],
                stockRanges['101-200'],
                stockRanges['200+']
              ],
              backgroundColor: ['#F44336', '#FF9800', '#FFC107', '#4CAF50', '#2196F3'],
              borderRadius: 8,
              borderSkipped: false
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { stepSize: 1 },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: { grid: { display: false } }
            }
          }
        });
      }
    }

    return () => {
      if (stockStatusChartInstance.current) {
        stockStatusChartInstance.current.destroy();
        stockStatusChartInstance.current = null;
      }
      if (stockDistributionChartInstance.current) {
        stockDistributionChartInstance.current.destroy();
        stockDistributionChartInstance.current = null;
      }
    };
  }, [products, stats, loading]);

  // Ouvrir la modale d'édition
  const handleEditStock = (product: StockItem) => {
    setEditingProduct(product);
    setEditQuantity(product.stock_quantity || 0);
    setEditMinStock(product.minStock || 50);
  };

  // Sauvegarder les modifications
  const handleSaveStock = async () => {
    if (!editingProduct) return;

    try {
      // Mettre à jour le produit via l'API
      await productService.update(editingProduct.id, {
        stock_quantity: editQuantity,
        stock_status: editQuantity === 0 ? 'out-of-stock' : 
                     editQuantity <= editMinStock ? 'pending' : 'in-stock'
      });

      // Sauvegarder le seuil minimum
      saveMinStockToStorage(editingProduct.id, editMinStock);

      // Mettre à jour l'état local
      setProducts(products.map(p => 
        p.id === editingProduct.id 
          ? { ...p, stock_quantity: editQuantity, minStock: editMinStock }
          : p
      ));

      setEditingProduct(null);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du stock:', error);
      alert(t('adminStock.updateError'));
    }
  };

  const getStatusClass = (status: StockStatus) => {
    const statusMap: Record<StockStatus, string> = {
      'in-stock': 'in-stock',
      'low': 'low',
      'out': 'out'
    };
    return statusMap[status] || 'in-stock';
  };

  const getStatusText = (status: StockStatus) => {
    const statusMap: Record<StockStatus, string> = {
      'in-stock': t('adminStock.inStock'),
      'low': t('adminStock.low'),
      'out': t('adminStock.outOfStock')
    };
    return statusMap[status] || 'En stock';
  };

  const getStatusBadge = (status: StockStatus) => {
    const statusMap: Record<StockStatus, { text: string; variant: 'success' | 'error' | 'warning' | 'info' | 'neutral' }> = {
      'in-stock': { text: t('adminStock.inStock'), variant: 'success' },
      'low': { text: t('adminStock.chartLow'), variant: 'warning' },
      'out': { text: t('adminStock.outOfStock'), variant: 'error' },
    };
    const config = statusMap[status] || statusMap['in-stock'];
    return <Badge variant={config.variant}>{config.text}</Badge>;
  };

  const columns: ResponsiveTableColumn<StockItem>[] = [
    {
      key: 'product',
      label: t('adminStock.product'),
      render: (product) => (
        <div className="stock-product-cell">
          <img 
            src={product.image_url || '/images/bottle.png'} 
            alt={product.name} 
            className="product-thumbnail" 
            onError={(e) => { (e.target as HTMLImageElement).src = '/images/bottle.png'; }} 
          />
          <div>
            <div className="product-name">{product.name}</div>
            <div className="product-category">{product.category || t('adminStock.noCategory')}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'sku',
      label: 'SKU',
      render: (product) => <div className="table-cell-name">{product.sku || 'N/A'}</div>,
      mobileHidden: true,
    },
    {
      key: 'quantity',
      label: t('adminStock.quantity'),
      render: (product) => (
        <div className="stock-quantity">
          <span className="quantity-value">
            {product.stock_unlimited ? '∞' : (product.stock_quantity || 0)}
          </span>
          <span className="quantity-unit">{t('adminStock.units')}</span>
        </div>
      ),
    },
    {
      key: 'minStock',
      label: t('adminStock.minStockShort'),
      render: (product) => <div className="table-cell-number">{product.minStock || 50}</div>,
      mobileHidden: true,
    },
    {
      key: 'status',
      label: t('adminStock.statusCol'),
      render: (product) => getStatusBadge(calculateStockStatus(product)),
    },
    {
      key: 'updated_at',
      label: t('adminStock.lastUpdate'),
      render: (product) => (
        <div className="table-cell-text">
          {product.updated_at 
            ? new Date(product.updated_at).toLocaleDateString('fr-FR')
            : 'N/A'}
        </div>
      ),
      mobileHidden: true,
    },
    {
      key: 'actions',
      label: t('adminStock.actions'),
      mobileHidden: true,
      render: (product) => (
        <div className="product-actions">
          <button
            className="action-btn action-view"
            title={t('adminStock.viewDetail')}
            onClick={() => setDetailModalOpen(product)}
            aria-label={t('adminStock.viewDetail')}
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          <button
            className="action-btn action-delete"
            title={t('adminStock.delete')}
            onClick={() => setDeleteModalOpen(product)}
            aria-label={t('adminStock.delete')}
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ color: '#999' }}>{t('adminStock.loading')}</div>
              </div>
    );
  }

  return (
    <>

    {error && (
              <Alert
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            {/* KPI Cards */}
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ShoppingBagIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                    <h3 className="kpi-title">{t('adminStock.totalProducts')}</h3>
                  </div>
                  <span className="kpi-index">#1</span>
                </div>
                <div className="kpi-value">{stats.total.toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminStock.registered')}</div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CheckCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#4CAF50' }} />
                    <h3 className="kpi-title">{t('adminStock.inStock')}</h3>
                  </div>
                  <span className="kpi-index">#2</span>
                </div>
                <div className="kpi-value">{stats.inStock.toLocaleString('fr-FR')}</div>
                <div className="kpi-change positive">
                  <span className="change-icon">✓</span>
                  <span className="change-value">{t('adminStock.normal')}</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ExclamationTriangleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#FF9800' }} />
                    <h3 className="kpi-title">{t('adminStock.chartLow')}</h3>
                  </div>
                  <span className="kpi-index">#3</span>
                </div>
                <div className="kpi-value">{stats.low.toLocaleString('fr-FR')}</div>
                <div className="kpi-change negative">
                  <span className="change-icon">⚠</span>
                  <span className="change-value">{t('adminStock.alert')}</span>
                </div>
              </div>
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <XCircleIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F44336' }} />
                    <h3 className="kpi-title">{t('adminStock.chartOut')}</h3>
                  </div>
                  <span className="kpi-index">#4</span>
                </div>
                <div className="kpi-value">{stats.out.toLocaleString('fr-FR')}</div>
                <div className="kpi-change negative">
                  <span className="change-icon">✗</span>
                  <span className="change-value">{t('adminStock.urgent')}</span>
                </div>
              </div>
            </div>

            {/* Graphiques de stock */}
            <div className="charts-row-responsive" style={{ marginBottom: '24px' }}>
              <div className="chart-card">
                <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>{t('adminStock.chartStatus')}</h4>
                {loading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>{t('adminStock.loading')}</div>
                ) : (
                  <div style={{ height: '300px', position: 'relative' }}>
                    <canvas ref={stockStatusChartRef}></canvas>
                  </div>
                )}
              </div>
              <div className="chart-card">
                <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>{t('adminStock.chartDistribution')}</h4>
                {loading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>{t('adminStock.loading')}</div>
                ) : (
                  <div style={{ height: '300px', position: 'relative' }}>
                    <canvas ref={stockDistributionChartRef}></canvas>
                  </div>
                )}
              </div>
            </div>

            {/* Stock Table */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('adminStock.detailsTitle', { count: filteredProducts.length })}</h3>
                <div className="flex gap-2 items-center">
                  {(searchTerm || statusFilter !== 'all' || categoryFilter !== 'all' || minQuantityFilter || maxQuantityFilter) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setStatusFilter('all');
                        setCategoryFilter('all');
                        setMinQuantityFilter('');
                        setMaxQuantityFilter('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminStock.resetFilters')}
                    </button>
                  )}
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      const csv = [
                        [t('adminStock.product'), 'SKU', t('adminStock.quantity'), t('adminStock.minStockShort'), t('adminStock.statusCol')].join(','),
                        ...filteredProducts.map(p => [
                          p.name,
                          p.sku || '',
                          p.stock_quantity || 0,
                          p.minStock || 50,
                          getStatusText(calculateStockStatus(p))
                        ].join(','))
                      ].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `stock_${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                    }}
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                    {t('adminStock.export')}
                  </button>
                </div>
              </div>

              {/* Filtres et recherche */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminStock.search')}</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('adminStock.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminStock.status')}</label>
                  <select 
                    className="form-select" 
                    value={statusFilter} 
                    onChange={(e) => setStatusFilter(e.target.value as StockStatus | 'all')}
                  >
                    <option value="all">{t('adminStock.all')}</option>
                    <option value="in-stock">{t('adminStock.inStock')}</option>
                    <option value="low">{t('adminStock.chartLow')}</option>
                    <option value="out">{t('adminStock.outOfStock')}</option>
                  </select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <label className="form-label">{t('adminStock.category')}</label>
                  <select 
                    className="form-select" 
                    value={categoryFilter} 
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">{t('adminStock.allCategories')}</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminStock.minQuantity')}</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={t('adminStock.zero')}
                    min="0"
                    value={minQuantityFilter}
                    onChange={(e) => setMinQuantityFilter(e.target.value)}
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminStock.maxQuantity')}</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={t('adminStock.infinity')}
                    min="0"
                    value={maxQuantityFilter}
                    onChange={(e) => setMaxQuantityFilter(e.target.value)}
                  />
                </div>
              </div>

              <ResponsiveTable
                columns={columns}
                data={paginatedProducts}
                loading={loading}
                loadingMessage={t('adminStock.loading')}
                emptyMessage={
                  searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
                    ? t('adminProductsList.noFilters')
                    : t('adminProductsList.empty')
                }
                mobileCardTitle={(product) => (
                  <div>
                    <div className="table-cell-name mb-1">{product.name}</div>
                    <div className="text-xs text-body">{product.sku || 'N/A'} • {product.category || t('adminStock.noCategory')}</div>
                  </div>
                )}
                mobileCardActions={(product) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="action-btn action-view"
                      title={t('adminStock.viewDetail')}
                      onClick={() => setDetailModalOpen(product)}
                      aria-label={t('adminStock.viewDetail')}
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      className="action-btn action-delete"
                      title={t('adminStock.delete')}
                      onClick={() => setDeleteModalOpen(product)}
                      aria-label={t('adminStock.delete')}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
                tableClassName="products-table"
              />

              {/* Pagination */}
              {!loading && totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminStock.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredProducts.length), total: filteredProducts.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminStock.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminStock.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal de détail du produit */}
            {detailModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setDetailModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="detail-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '90%' }}>
                  <div className="modal-header">
                    <h3 id="detail-modal-title" className="modal-title">
                      {t('adminStock.detailTitle', { name: detailModalOpen.name })}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setDetailModalOpen(null)}
                      aria-label={t('adminStock.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Image du produit */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminStock.productImage')}
                        </h4>
                        <img 
                          src={detailModalOpen.image_url || '/images/bottle.png'} 
                          alt={detailModalOpen.name}
                          style={{ 
                            width: '200px', 
                            height: '200px', 
                            objectFit: 'cover', 
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0'
                          }}
                          onError={(e) => { (e.target as HTMLImageElement).src = '/images/bottle.png'; }}
                        />
                      </div>

                      {/* Informations générales */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminStock.generalInfo')}
                        </h4>
                        <div className="stock-detail-grid">
                          <div>
                            <strong>{t('adminClients.name')}:</strong> {detailModalOpen.name}
                          </div>
                          <div>
                            <strong>{t('adminStock.sku')}:</strong> {detailModalOpen.sku || t('adminStock.na')}
                          </div>
                          <div>
                            <strong>{t('adminStock.category')}:</strong> {detailModalOpen.category || t('adminStock.noCategory')}
                          </div>
                          <div>
                            <strong>{t('adminProductsList.price')}:</strong> {detailModalOpen.price != null && detailModalOpen.price !== '' && Number.isFinite(Number(detailModalOpen.price)) ? `${Number(detailModalOpen.price).toFixed(2)} €` : t('adminStock.na')}
                          </div>
                        </div>
                      </div>

                      {/* Informations de stock */}
                      <div>
                        <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                          {t('adminStock.stockInfo')}
                        </h4>
                        <div className="stock-detail-grid">
                          <div>
                            <strong>{t('adminStock.quantityInStock')}:</strong>
                            <div style={{ marginTop: '4px', fontSize: '18px', fontWeight: '600' }}>
                              {detailModalOpen.stock_unlimited ? `∞ (${t('adminProductsList.unlimited')})` : (detailModalOpen.stock_quantity || 0)}
                            </div>
                          </div>
                          <div>
                            <strong>{t('adminStock.minStock')}:</strong>
                            <div style={{ marginTop: '4px' }}>
                              {detailModalOpen.minStock || 50}
                            </div>
                          </div>
                          <div>
                            <strong>{t('adminStock.statusCol')}:</strong>
                            <div style={{ marginTop: '8px' }}>
                              {getStatusBadge(calculateStockStatus(detailModalOpen))}
                            </div>
                          </div>
                          <div>
                            <strong>{t('adminStock.unlimitedStock')}:</strong>
                            <div style={{ marginTop: '4px' }}>
                              {detailModalOpen.stock_unlimited ? (
                                <Badge variant="success">{t('adminAddProduct.yes')}</Badge>
                              ) : (
                                <Badge variant="neutral">{t('adminAddProduct.no')}</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      {detailModalOpen.description && (
                        <div>
                          <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                            {t('adminStock.description')}
                          </h4>
                          <div style={{ 
                            padding: '16px', 
                            background: '#f9f9f9', 
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word'
                          }}>
                            {detailModalOpen.description}
                          </div>
                        </div>
                      )}

                      {/* Dates */}
                      <div className="stock-detail-grid" style={{ gap: '16px' }}>
                        <div>
                          <strong>{t('adminStock.createdAt')}:</strong>
                          <div style={{ marginTop: '4px', color: '#666' }}>
                            {detailModalOpen.created_at 
                              ? new Date(detailModalOpen.created_at).toLocaleDateString('fr-FR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'N/A'}
                          </div>
                        </div>
                        {detailModalOpen.updated_at && detailModalOpen.updated_at !== detailModalOpen.created_at && (
                          <div>
                            <strong>{t('adminStock.lastModified')}:</strong>
                            <div style={{ marginTop: '4px', color: '#666' }}>
                              {new Date(detailModalOpen.updated_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDetailModalOpen(null)}
                    >
                      {t('adminStock.close')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setDetailModalOpen(null);
                        handleEditStock(detailModalOpen);
                      }}
                    >
                      {t('adminStock.editStock')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => setDeleteModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 id="delete-modal-title" className="modal-title">
                      {t('adminStock.deleteProduct')}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => setDeleteModalOpen(null)}
                      aria-label={t('adminStock.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <p>
                      {t('adminStock.deleteConfirm', { name: deleteModalOpen.name })}
                    </p>
                    <p style={{ marginTop: '12px', color: '#F44336', fontWeight: '600' }}>
                      {t('adminStock.irreversible')}
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setDeleteModalOpen(null)}
                      disabled={deleting}
                    >
                      {t('adminStock.cancel')}
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleDeleteProduct}
                      disabled={deleting}
                      style={{ backgroundColor: '#F44336', borderColor: '#F44336' }}
                    >
                      {deleting ? t('adminStock.deleting') : t('adminStock.delete')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modale d'édition */}
            {editingProduct && (
              <div 
                className="modal-overlay" 
                onClick={() => setEditingProduct(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 id="modal-title" className="modal-title">{t('adminStock.editStockTitle', { name: editingProduct.name })}</h3>
                    <button 
                      className="modal-close"
                      onClick={() => setEditingProduct(null)}
                      aria-label={t('adminStock.close')}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">{t('adminStock.quantityLabel')}</label>
                      <input
                        type="number"
                        min="0"
                        value={editQuantity}
                        onChange={(e) => setEditQuantity(parseInt(e.target.value) || 0)}
                        className="form-input"
                      />
                      {editingProduct.stock_unlimited && (
                        <div className="form-help-text">
                          {t('adminStock.unlimitedEnabled')}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('adminStock.minThreshold')}</label>
                      <input
                        type="number"
                        min="0"
                        value={editMinStock}
                        onChange={(e) => setEditMinStock(parseInt(e.target.value) || 0)}
                        className="form-input"
                      />
                      <div className="form-help-text">
                        {t('adminStock.thresholdHelp')}
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setEditingProduct(null)}
                    >
                      {t('adminStock.cancel')}
                    </button>
                    <button 
                      className="btn btn-primary"
                      onClick={handleSaveStock}
                    >
                      {t('adminStock.save')}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};
